import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/TalentAgencyBusinessAssociateCompanies.graphql';
import { Query, QuerytalentAgencyBusinessAssociateCompaniesArgs } from '../../__generated__/globalTypes';

export const useTalentAgencyBusinessAssociateCompaniesQuery = (
  options?: QueryHookOptions<Query, QuerytalentAgencyBusinessAssociateCompaniesArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QuerytalentAgencyBusinessAssociateCompaniesArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useTalentAgencyBusinessAssociateCompaniesLazyQuery = (
  options?: LazyQueryHookOptions<Query, QuerytalentAgencyBusinessAssociateCompaniesArgs>
): QueryResult<Query, QuerytalentAgencyBusinessAssociateCompaniesArgs> & {
  getTalentAgencyBusinessAssociateCompanies: LazyQueryExecFunction<
    Query,
    QuerytalentAgencyBusinessAssociateCompaniesArgs
  >;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getTalentAgencyBusinessAssociateCompanies, result] = useLazyQuery<
    Query,
    QuerytalentAgencyBusinessAssociateCompaniesArgs
  >(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getTalentAgencyBusinessAssociateCompanies, ...result };
};

export const useTalentAgencyBusinessAssociateCompaniesPromiseQuery = () => {
  const client = useApolloClient();

  const getTalentAgencyBusinessAssociateCompanies = useCallback(
    (options?: Omit<QueryOptions<QuerytalentAgencyBusinessAssociateCompaniesArgs, Query>, 'query'>) =>
      client.query<Query, QuerytalentAgencyBusinessAssociateCompaniesArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getTalentAgencyBusinessAssociateCompanies };
};
// Suspense Query Hooks
export const useTalentAgencyBusinessAssociateCompaniesSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QuerytalentAgencyBusinessAssociateCompaniesArgs>
) => {
  const result = useSuspenseQuery<Query, QuerytalentAgencyBusinessAssociateCompaniesArgs>(QUERY, {
    ...options
  });

  return result;
};
